/**
 * A flat `{key: number}` RTL pricing section — base rates, term adjustments and
 * class adjustments all share this shape.
 *
 * Two number formats, and they must not be confused:
 *   format="rate"    — annual decimal fraction stored, shown as a percent
 *                      (0.0725 ⇄ 7.250%)
 *   format="percent" — the value IS a percent, stored verbatim (1.25 = +1.25%).
 *                      (2026-08-19: basis points retired from RTL pricing — the
 *                      API stores exactly what the user types.)
 *
 * The editor is a table: these maps are short but they ARE records (one row per
 * product / class / term), and reading four of them down a column beats reading
 * four stacked pairs of form fields. Keys are editable because entries are added
 * and removed by key, not by position.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import AddRounded from "@mui/icons-material/AddRounded";

import Flx from "../../../components/layout/Flx";
import { removeColumn } from "../shared/adminTableActions";
import {
  PRICING_GRID_COL_DEF,
  PricingField,
  PricingGrid,
  PricingSection,
  SectionEmpty,
} from "./pricingUi";

// Both formats are EDITED AS A PERCENT. Only the stored scale differs: rates are
// decimal fractions (0.0725 = 7.25%) so they convert; adjustments are ALREADY a
// percent (1.25 = 1.25%) and store verbatim. The conversion lives here and
// nowhere else.
const toDisplay = (v, format) => {
  if (v == null || v === "") return null;
  return format === "rate"
    ? Number((Number(v) * 100).toFixed(4))
    : // Percent is stored verbatim — display as-is.
      Number(Number(v).toFixed(4));
};

const toStored = (n, format) => {
  if (n == null || !Number.isFinite(Number(n))) return null;
  return format === "rate"
    ? Number((Number(n) / 100).toFixed(6))
    : // Percent is stored verbatim — send exactly what the user typed.
      Number(Number(n).toFixed(4));
};

const isBlank = (v) => v == null || String(v).trim() === "";

const numericParser = (p) => {
  const v = String(p.newValue ?? "").trim();
  if (v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : p.oldValue;
};

export default function RateMapSection({
  id,
  title,
  helper,
  keyLabel,
  valueLabel,
  format,
  data,
  version,
  lastEdit,
  onHistory,
  busy,
  onSave,
}) {
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");
  const [dirty, setDirty] = useState(false);

  // Shared by the initial seed and the reset button — both go back to the saved
  // `data` prop, which is the only source of truth for "unchanged".
  const reseed = useCallback(() => {
    setRows(
      Object.entries(data || {}).map(([k, v], i) => ({
        __i: i,
        key: k,
        value: toDisplay(v, format),
      })),
    );
    setDirty(false);
    setNote("");
  }, [data, format]);

  useEffect(reseed, [reseed]);

  const unit = "%";

  const columnDefs = useMemo(
    () => [
      {
        headerName: keyLabel,
        field: "key",
        minWidth: 200,
        flex: 1,
        editable: true,
        // A blank in a just-added row prompts instead of showing a dash.
        cellClass: (p) => (isBlank(p.value) ? "ph" : "key"),
        valueFormatter: (p) =>
          isBlank(p.value) ? `Enter a ${keyLabel.toLowerCase()}…` : p.value,
      },
      {
        headerName: `${valueLabel} (${unit})`,
        field: "value",
        width: 200,
        editable: true,
        cellClass: (p) => (isBlank(p.value) ? "num ph" : "num"),
        headerClass: "num",
        valueParser: numericParser,
        // Four decimals on both scales — the desk quotes to the basis point, and
        // a fixed width keeps the column reading as one stack of figures.
        valueFormatter: (p) =>
          isBlank(p.value)
            ? "Enter a value…"
            : `${Number(p.value).toFixed(4)} ${unit}`,
      },
      removeColumn((p) => {
        setRows((prev) => prev.filter((r) => r.__i !== p.data.__i));
        setDirty(true);
      }),
    ],
    [keyLabel, valueLabel, unit],
  );

  const save = useCallback(() => {
    const next = {};
    rows.forEach((r) => {
      const k = String(r.key ?? "").trim();
      if (!k) return;
      const v = toStored(r.value, format);
      if (v != null) next[k] = v;
    });
    onSave?.(next, note || `${title} updated`)
      .then(() => setDirty(false))
      .catch(() => {});
  }, [rows, format, onSave, note, title]);

  return (
    <PricingSection
      id={id}
      title={title}
      desc={helper}
      version={version}
      lastEdit={lastEdit}
      onHistory={onHistory}
      dense
    >
      {!rows.length ? (
        <SectionEmpty>Nothing configured — add a row.</SectionEmpty>
      ) : (
        <PricingGrid rowCount={rows.length} maxRows={10}>
          <AgGridReact
            rowData={rows}
            columnDefs={columnDefs}
            singleClickEdit
            defaultColDef={PRICING_GRID_COL_DEF}
            getRowId={(p) => String(p.data.__i)}
            onCellValueChanged={(e) => {
              setRows((prev) =>
                prev.map((r) =>
                  r.__i === e.data.__i
                    ? { ...r, [e.colDef.field]: e.newValue }
                    : r,
                ),
              );
              setDirty(true);
            }}
            stopEditingWhenCellsLoseFocus
          />
        </PricingGrid>
      )}

      {/* Add sits under the table it appends to. The commit/save controls only
          exist once there is something to commit. */}
      {/* Aligned on the controls, not their labels — the commit field is two
          elements tall now, the buttons one. */}
      <Flx g={1.5} sx={{ px: 2.5, pt: 0, pb: 2, alignItems: "flex-end" }} wrap>
        <Button
          size="small"
          variant="text"
          color="success"
          startIcon={<AddRounded />}
          onClick={() => {
            setRows((prev) => [
              ...prev,
              {
                __i: prev.reduce((m, r) => Math.max(m, r.__i), -1) + 1,
                key: "",
                value: null,
              },
            ]);
            setDirty(true);
          }}
        >
          Add row
        </Button>

        {dirty ? (
          <>
            <PricingField
              label="Commit message"
              sx={{ minWidth: 240, flex: 1 }}
            >
              <TextField
                size="small"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
              />
            </PricingField>
            <Button variant="text" onClick={reseed} disabled={busy}>
              Reset changes
            </Button>
            <Button variant="contained" onClick={save} disabled={busy}>
              Save section
            </Button>
          </>
        ) : null}
      </Flx>
    </PricingSection>
  );
}

RateMapSection.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  helper: PropTypes.string,
  version: PropTypes.node,
  lastEdit: PropTypes.object,
  onHistory: PropTypes.func,
  keyLabel: PropTypes.string,
  valueLabel: PropTypes.string,
  format: PropTypes.oneOf(["rate", "percent"]).isRequired,
  data: PropTypes.object,
  busy: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};
